'use client';

import { FC, CSSProperties, useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import {
  ChevronDownIcon,
  LinkIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import { Avatar } from '@/components/atoms/Avatar';
import { CommentEditor } from '@/components/organisms/CommentEditor';
import type { CommentData } from '@/components/template/BlogDetailTemplate/ArticleComments';
import { getUserAuthHeader } from '@/lib/schema/user/client-fetch';
import { AFTER_LOGIN_REDIRECT_KEY } from '@/lib/constants';
import { showToast } from '@/lib/toast';
import { DropdownProps } from '@/components/atoms/Dropdown';
import { CommentHeader, CommentContent } from './Comment';

/* ───────── node type ────────────────────────────────────────────── */
export interface CommentThreadNode {
  postId: string;
  comment: {
    id: string;
    user: { username: string; avatar?: string };
    createdAt: string;
    content: string;
    upvotes: number;
    downvotes: number;
    votedFor: 'upvote' | 'downvote' | null;
    onVote: (v: 'upvote' | 'downvote') => Promise<boolean>;
    commentActions: DropdownProps['actions'];
  };
  replies: CommentThreadNode[];
  remainingReplies: number;
  sort?: string;
  isHighlighted?: boolean;
  topLevelParentId?: string; // ID of the top-level parent comment
  isReported?: boolean; // Flag to track if the comment has been reported
  fetchMoreReplies: (
    skip: number,
    sort?: string
  ) => Promise<CommentThreadNode[]>;
  onSubmitReply: (body: string) => Promise<void>;
}

/* ───────── component ───────────────────────────────────────────── */
export const CommentThread: FC<CommentThreadNode> = (props) => {
  const {
    postId,
    comment,
    replies: initReplies,
    remainingReplies: initRemaining,
    fetchMoreReplies,
    onSubmitReply,
    sort,
    isHighlighted,
    isReported,
  } = props;

  const [replies, setReplies] = useState(initReplies);
  const [remaining, setRemaining] = useState(initRemaining);
  const [loading, setLoading] = useState(false);

  const [showEd, setShowEd] = useState(false);
  const [body, setBody] = useState('');
  const [votedFor, setVotedFor] = useState(comment.votedFor);
  const [upvotes, setUpvotes] = useState(comment.upvotes);
  const [downvotes, setDownvotes] = useState(comment.downvotes);

  // Add ref for the latest reply
  const latestReplyRef = useRef<string | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  /* keep local state in sync when parent refreshes */
  useEffect(() => {
    setReplies(initReplies);
    setRemaining(initRemaining);
  }, [initReplies, initRemaining]);

  // Keep vote state in sync with parent data
  useEffect(() => {
    setUpvotes(comment.upvotes);
    setDownvotes(comment.downvotes);
    setVotedFor(comment.votedFor);
  }, [comment.upvotes, comment.downvotes, comment.votedFor]);

  // Add effect to scroll to the latest reply
  useEffect(() => {
    if (latestReplyRef.current) {
      // Find the added comment by its ID
      const commentElements = document.querySelectorAll(
        `[data-comment-id="${latestReplyRef.current}"]`
      );
      if (commentElements.length > 0) {
        // Scroll the latest comment into view with smooth behavior
        commentElements[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        // Reset the ref
        latestReplyRef.current = null;
      }
    }
  }, [replies]); // Trigger when replies change
  /* ───────── child wrapper ------------------------------------- */
  const Child: FC<{
    child: CommentThreadNode;
    isLast: boolean;
    horzLine: string;
  }> = ({ child, isLast, horzLine }) => (
    <>
      <div className="flex flex-col items-center">
        {!isLast && <div className="h-full w-px bg-neutral-300" />}
      </div>
      <div className={clsx('relative', { 'pb-xl': !isLast })}>
        <CommentThread {...child} />
        <div
          className={clsx(
            horzLine,
            'top-0 h-[calc(var(--avatar-size)/2)] translate-y-[calc(-100%+calc(var(--avatar-size)/2))]'
          )}
        />
      </div>
    </>
  );

  /* ------------ pagination -------------------------------------- */
  const loadNext = async () => {
    if (loading || remaining === 0) return;
    setLoading(true);

    try {
      // Fetch more replies with sort parameter
      const fresh = await fetchMoreReplies(replies.length, sort);
      const ids = new Set(replies.map((r) => r.comment.id));
      const unique = fresh.filter((n) => !ids.has(n.comment.id));

      // Add new unique replies to the list
      const updatedReplies = [...replies, ...unique];
      setReplies(updatedReplies);

      // Recompute remaining direct children after new load:
      // 1. Sum all descendants of all children we now have
      const totalChildDescendants = updatedReplies.reduce(
        (sum, child) => sum + child.remainingReplies + child.replies.length,
        0
      );

      // 2. Compare with total reported by server
      const totalDescendants = initRemaining + replies.length;

      // 3. If we've loaded all or almost all expected replies, or got empty/small batch
      if (
        unique.length === 0 ||
        fresh.length < 10 ||
        updatedReplies.length >= totalDescendants ||
        totalChildDescendants >= totalDescendants - 5 || // Allow small discrepancy
        replies.length > 10
      ) {
        // If we've already loaded a significant number, assume we're done
        setRemaining(0);
      } else {
        // Otherwise just decrement by what we loaded
        setRemaining((prev) => {
          const newRemaining = Math.max(0, prev - unique.length);
          // Force to zero if very small - likely a counting error
          return newRemaining <= 2 ? 0 : newRemaining;
        });
      }
    } catch (err) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  /* ------------ reply (optimistic) ------------------------------ */
  const submit = async () => {
    if (!body.trim()) return;

    try {
      await onSubmitReply(body);
      setBody('');
      setShowEd(false);
    } catch (error) {
      showToast('Error loading comments', { type: 'error' });
    }
  };

  /* ------------ handle vote ------------------------------------- */
  const handleVote = async (vote: 'upvote' | 'downvote') => {
    // Store previous state for rollback if needed
    const prevVotedFor = votedFor;
    const prevUpvotes = upvotes;
    const prevDownvotes = downvotes;

    // Optimistically update UI based on vote action
    if (votedFor === vote) {
      // Removing vote
      setVotedFor(null);
      if (vote === 'upvote') {
        setUpvotes((prev) => Math.max(0, prev - 1));
      } else {
        setDownvotes((prev) => Math.max(0, prev - 1));
      }
    } else if (votedFor === null) {
      // Adding new vote
      setVotedFor(vote);
      if (vote === 'upvote') {
        setUpvotes((prev) => prev + 1);
      } else {
        setDownvotes((prev) => prev + 1);
      }
    } else {
      // Changing vote (e.g., from upvote to downvote)
      setVotedFor(vote);
      if (vote === 'upvote') {
        setUpvotes((prev) => prev + 1);
        setDownvotes((prev) => Math.max(0, prev - 1));
      } else {
        setDownvotes((prev) => prev + 1);
        setUpvotes((prev) => Math.max(0, prev - 1));
      }
    }

    // Make the actual API call
    try {
      const success = await comment.onVote(vote);
      if (!success) {
        // If API call fails, revert changes
        setVotedFor(prevVotedFor);
        setUpvotes(prevUpvotes);
        setDownvotes(prevDownvotes);
      }
    } catch (error) {
      // If there's an exception, also revert changes
      setVotedFor(prevVotedFor);
      setUpvotes(prevUpvotes);
      setDownvotes(prevDownvotes);
    }
  };

  /* ------------ layout consts ----------------------------------- */
  const AV = 32;
  const horz =
    'absolute left-0 w-[calc(calc(calc(var(--avatar-size)/2)+var(--x-gap))+0.5px)] -translate-x-full rounded-bl-small border-b border-l border-neutral-300';

  /* ------------ render ------------------------------------------ */
  return (
    <div
      className={clsx('flex flex-col gap-xs', {
        'animate-pulse-highlight rounded-small bg-blue-light': isHighlighted,
      })}
      style={{ '--avatar-size': `${AV}px`, '--x-gap': '12px' } as CSSProperties}
      ref={threadRef}
      data-comment-id={comment.id}
    >
      {/* header */}
      <div className="grid grid-cols-[var(--avatar-size)_1fr] gap-x-[--x-gap]">
        <Avatar size={AV} imageIconSrc={comment.user.avatar} />
        <CommentHeader
          user={comment.user}
          createdAt={comment.createdAt}
          commentActions={comment.commentActions}
        />
      </div>

      {/* body + editor + children */}
      <div className="grid grid-cols-[var(--avatar-size)_1fr] gap-x-[var(--x-gap)]">
        <div className="flex flex-col items-center">
          {replies.length > 0 && <div className="h-full w-px bg-neutral-300" />}
        </div>

        <div className="flex flex-col gap-xl pb-xl">
          {isReported ? (
            <p className="text-body2Light italic text-neutral-600">
              This comment has been reported and is hidden from view.
            </p>
          ) : (
            <CommentContent
              content={comment.content}
              upvotes={upvotes}
              downvotes={downvotes}
              votedFor={votedFor}
              onVote={handleVote}
              onReply={() => setShowEd(true)}
            />
          )}

          {showEd && !isReported && (
            <CommentEditor
              placeholder={`Reply to @${comment.user.username}`}
              value={body}
              onChange={setBody}
              onSubmit={submit}
              onCancel={() => setShowEd(false)}
            />
          )}
        </div>

        {/* children */}
        {replies.map((c, i) => (
          <Child
            key={c.comment.id}
            child={{
              ...c,
              postId,
              sort,
            }}
            isLast={i === replies.length - 1 && remaining === 0}
            horzLine={horz}
          />
        ))}

        {/* "Show n replies" button */}
        {remaining > 0 && (
          <>
            <div className="flex flex-col items-center" />
            <div className="relative">
              <button
                onClick={loadNext}
                disabled={loading}
                className="flex items-center gap-xs text-body2Medium text-blue-medium"
              >
                {loading
                  ? 'Loading…'
                  : `Show ${remaining} ${remaining === 1 ? 'reply' : 'replies'}`}
                <ChevronDownIcon className="size-l stroke-[3px]" />
              </button>
              <div className={clsx(horz, 'bottom-1/2 h-[12px]')} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CommentThread;

/* ───────── mapper -------------------------------------------------- */
export const toThreadNode = (
  c: CommentData,
  postId: string,
  replyHandler: (parentId: string, body: string) => Promise<void>,
  sort?: string,
  topLevelParentId?: string
): CommentThreadNode => {
  // If there's no topLevelParentId provided, this is a top-level comment
  // So set topLevelParentId to the current comment's ID
  const parentId = topLevelParentId || c._id;

  // Map immediate replies to ThreadNodes
  const preview = (c.replies ?? []).map((r: CommentData) =>
    toThreadNode(r, postId, replyHandler, sort, parentId)
  );

  // Total descendants (all depths)
  const totalDescendants = c.replyCount ?? 0;

  // Sum of every child's descendants (child.replyCount) – grandchildren etc.
  const descendantsOfChildren = (c.replies ?? []).reduce(
    (sum: number, child: CommentData) => sum + (child.replyCount ?? 0),
    0
  );

  // Number of direct children according to the server
  const directChildren = Math.max(0, totalDescendants - descendantsOfChildren);

  // Hidden direct children that have not been loaded yet
  const remainingDirectReplies = Math.max(
    0,
    directChildren - (c.replies ?? []).length
  );

  // Create comment action dropdown items
  const commentActions: DropdownProps['actions'] = [
    [
      {
        onClick: () => {
          // Copy link logic would go here
          // Remove any existing hash from the URL before appending the comment ID
          const baseUrl = window.location.href.split('#')[0];
          // Use the top-level parent ID for the link to ensure all nested comments are loaded
          const idToUse = parentId;
          navigator.clipboard.writeText(`${baseUrl}#comment-${idToUse}`);
          showToast('Link copied to clipboard', { type: 'success' });
        },
        type: 'neutral' as const,
        label: 'Copy link',
        icon: LinkIcon,
      },
      {
        onClick: () => {
          // Report logic would go here
          // In a real application, this would make an API call to report the comment
          // For now, we'll just show a toast and update local state

          // Create a function to recursively mark the comment as reported
          const onReport = (threadId: string) => {
            const dispatchReportEvent = new CustomEvent('commentReported', {
              detail: { commentId: threadId },
            });
            window.dispatchEvent(dispatchReportEvent);
          };

          // Trigger the report event
          onReport(c._id);

          showToast('Comment reported and hidden from view', {
            type: 'success',
          });
        },
        type: 'neutral' as const,
        label: 'Report',
        icon: FlagIcon,
      },
    ],
  ];

  return {
    postId,
    comment: {
      id: c._id,
      user: {
        avatar: c.authorId
          ? `https://api.dicebear.com/7.x/initials/svg?seed=${c.authorId}`
          : '',
        username: c.authorId.split('@')[0], // Use ID instead of "Anonymous"
      },
      createdAt: c.createdAt,
      content: c.content,
      upvotes: c.upvoteCount ?? 0,
      downvotes: c.downvoteCount ?? 0,
      // Map backend vote types to frontend expected types
      votedFor: (() => {
        if (c.userVoteKind === 'up') {
          return 'upvote';
        }
        if (c.userVoteKind === 'down') {
          return 'downvote';
        }
        return null;
      })(),
      onVote: async (vote: 'upvote' | 'downvote') => {
        try {
          // Get auth header
          const authHeader = await getUserAuthHeader();

          if (!authHeader) {
            showToast('You must be logged in to vote', { type: 'error' });
            sessionStorage.setItem(
              AFTER_LOGIN_REDIRECT_KEY,
              window.location.pathname
            );
            window.location.href = '/login';
            return false;
          }

          // Convert UI vote type to API vote type
          const apiVoteType: 'up' | 'down' = vote === 'upvote' ? 'up' : 'down';

          if (c.userVoteKind === apiVoteType) {
            // If already voted this way, remove the vote
            const response = await fetch(
              `/api/community/comments/${c._id}/vote`,
              {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: authHeader,
                },
              }
            );

            return response.ok;
          }

          // Add or change vote
          const response = await fetch(
            `/api/community/comments/${c._id}/vote`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader,
              },
              body: JSON.stringify({ kind: apiVoteType }),
            }
          );

          return response.ok;
        } catch (error) {
          return false;
        }
      },
      commentActions,
    },
    replies: preview,
    // The remaining direct replies that we haven't loaded yet
    remainingReplies: remainingDirectReplies,
    sort, // Include sort in the returned object
    topLevelParentId: parentId, // Add the top-level parent ID to the node
    isReported: false, // Assuming the comment is not reported by default

    fetchMoreReplies: async (skip: number) => {
      try {
        // Get auth header
        const authHeader = await getUserAuthHeader();

        const r = await fetch(
          `/api/community/comments/${postId}?parentId=${c._id}&skip=${skip}&limit=10&sort=upvoteCount`,
          {
            headers: authHeader ? { Authorization: authHeader } : {},
          }
        );

        if (!r.ok) return [];
        const j = await r.json();
        return (j.content as CommentData[]).map((d: CommentData) =>
          toThreadNode(d, postId, replyHandler, sort, parentId)
        );
      } catch (error) {
        return [];
      }
    },

    onSubmitReply: (body) => replyHandler(c._id, body),
  };
};
